import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Package, Plus, Loader2, Save, X, Edit3, Trash2, DollarSign,
  Hash, Upload, FileSpreadsheet, Download, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import FileUpload from "@/components/dashboard/FileUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const LISTING_TYPES = [
  { value: "vehicles", label: "Vehicles" },
  { value: "real-estate", label: "Real Estate" },
  { value: "furniture", label: "Furniture" },
  { value: "rvs", label: "RVs" },
];

const TYPE_SPECIFIC_FIELDS = {
  vehicles: [
    { key: "make", label: "Make", ph: "e.g. Toyota" },
    { key: "model", label: "Model", ph: "e.g. Camry" },
    { key: "year", label: "Year", ph: "2024", type: "number" },
    { key: "mileage", label: "Mileage", ph: "15000", type: "number" },
    { key: "fuel_type", label: "Fuel Type", ph: "Gasoline" },
    { key: "transmission", label: "Transmission", ph: "Automatic" },
    { key: "color", label: "Exterior Color", ph: "Black" },
    { key: "interior_color", label: "Interior Color", ph: "Tan" },
    { key: "body_style", label: "Body Style", ph: "Sedan" },
    { key: "drivetrain", label: "Drivetrain", ph: "AWD" },
    { key: "engine", label: "Engine", ph: "2.5L 4-Cylinder" },
    { key: "trim", label: "Trim", ph: "XLE Premium" },
  ],
  "real-estate": [
    { key: "property_type", label: "Property Type", ph: "Single Family" },
    { key: "bedrooms", label: "Bedrooms", ph: "3", type: "number" },
    { key: "bathrooms", label: "Bathrooms", ph: "2", type: "number" },
    { key: "sqft", label: "Sq Ft", ph: "1800", type: "number" },
    { key: "lot_size", label: "Lot Size (acres)", ph: "0.25", type: "number" },
    { key: "year_built", label: "Year Built", ph: "2005", type: "number" },
    { key: "address", label: "Address", ph: "123 Main St" },
    { key: "parking", label: "Parking", ph: "2 Car Garage" },
    { key: "heating", label: "Heating", ph: "Central" },
    { key: "cooling", label: "Cooling", ph: "Central Air" },
  ],
  furniture: [
    { key: "brand", label: "Brand", ph: "e.g. Ashley" },
    { key: "category", label: "Category", ph: "Sofa" },
    { key: "material", label: "Material", ph: "Leather" },
    { key: "style", label: "Style", ph: "Modern" },
    { key: "room_type", label: "Room Type", ph: "Living Room" },
    { key: "dimensions", label: "Dimensions", ph: '84"W x 36"D x 34"H' },
    { key: "color", label: "Color", ph: "Brown" },
  ],
  rvs: [
    { key: "make", label: "Make", ph: "e.g. Winnebago" },
    { key: "model", label: "Model", ph: "Minnie Winnie" },
    { key: "year", label: "Year", ph: "2023", type: "number" },
    { key: "rv_type", label: "RV Type", ph: "Class C" },
    { key: "length", label: "Length (ft)", ph: "25", type: "number" },
    { key: "sleeps", label: "Sleeps", ph: "6", type: "number" },
    { key: "slideouts", label: "Slideouts", ph: "2", type: "number" },
    { key: "mileage", label: "Mileage", ph: "5000", type: "number" },
    { key: "fuel_type", label: "Fuel Type", ph: "Gasoline" },
    { key: "engine", label: "Engine", ph: "V8" },
  ],
};

const statusColors = { active: "text-[#10B981] bg-[#10B981]/10", sold: "text-[#EF4444] bg-[#EF4444]/10", inactive: "text-[#64748B] bg-white/5" };

export default function DashboardInventory() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [uploading, setUploading] = useState(false);
  const csvRef = useRef(null);
  const [bulkType, setBulkType] = useState("vehicles");
  const [form, setForm] = useState({ listing_type: "vehicles", title: "", description: "", price: "", condition: "", city: "", state: "", zip_code: "", image_url: "", status: "active", features: "" });

  useEffect(() => { fetchInventory(); }, [filterType]);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("listing_type", filterType);
      const res = await axios.get(`${API}/inventory?${params}`, { headers: getAuthHeader() });
      setItems(res.data?.items || []);
    } catch (err) { console.error("Failed:", err); }
    finally { setLoading(false); }
  };

  const openNew = () => {
    setForm({ listing_type: "vehicles", title: "", description: "", price: "", condition: "", city: "", state: "", zip_code: "", image_url: "", status: "active", features: "" });
    setEditingId(null);
    setShowEditor(true);
  };

  const openEdit = (item) => {
    setForm({
      ...item,
      price: item.price?.toString() || "",
      features: (item.features || []).join(", "),
      year: item.year?.toString() || "",
      mileage: item.mileage?.toString() || "",
      bedrooms: item.bedrooms?.toString() || "",
      bathrooms: item.bathrooms?.toString() || "",
      sqft: item.sqft?.toString() || "",
      sleeps: item.sleeps?.toString() || "",
      slideouts: item.slideouts?.toString() || "",
      length: item.length?.toString() || "",
    });
    setEditingId(item.id);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form };
    // Convert numerics
    if (payload.price) payload.price = parseFloat(payload.price);
    for (const f of ["year", "mileage", "bedrooms", "sqft", "sleeps", "slideouts", "length", "year_built"]) {
      if (payload[f]) payload[f] = parseInt(payload[f]);
    }
    if (payload.bathrooms) payload.bathrooms = parseFloat(payload.bathrooms);
    if (typeof payload.features === "string") {
      payload.features = payload.features.split(",").map(s => s.trim()).filter(Boolean);
    }
    try {
      if (editingId) {
        await axios.patch(`${API}/inventory/${editingId}`, payload, { headers: getAuthHeader() });
        toast.success("Item updated!");
      } else {
        await axios.post(`${API}/inventory`, payload, { headers: getAuthHeader() });
        toast.success("Item added to marketplace!");
      }
      setShowEditor(false);
      await fetchInventory();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/inventory/${id}`, { headers: getAuthHeader() });
      toast.success("Item removed");
      await fetchInventory();
    } catch (err) { toast.error("Failed to delete"); }
  };

  // Bulk Upload
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/inventory/bulk-upload?listing_type=${bulkType}`, formData, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success(`${res.data.created} items imported!`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      setShowBulk(false);
      await fetchInventory();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk upload failed"); }
    finally { setUploading(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/inventory/csv-template?listing_type=${bulkType}`, { headers: getAuthHeader() });
      const blob = new Blob([res.data.template], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${bulkType}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Failed to download template"); }
  };

  const typeFields = TYPE_SPECIFIC_FIELDS[form.listing_type] || [];

  if (loading && items.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-inventory">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Inventory</h1>
          <p className="text-[#94A3B8] mt-1">Manage marketplace listings — vehicles, real estate, furniture, RVs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowBulk(!showBulk)} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="bulk-upload-toggle">
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload
          </Button>
          <Button onClick={openNew} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>
      </div>

      {/* Bulk Upload Panel */}
      <AnimatePresence>
        {showBulk && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="bg-[#0F172A] border border-[#7C3AED]/30 rounded-xl p-6 overflow-hidden">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#7C3AED]" />
              CSV Bulk Upload
            </h2>
            <div className="flex flex-wrap items-end gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Listing Type</label>
                <select value={bulkType} onChange={(e) => setBulkType(e.target.value)}
                  className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm" data-testid="bulk-type-select">
                  {LISTING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <Button onClick={downloadTemplate} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="download-template">
                <Download className="w-4 h-4 mr-2" /> Download Template
              </Button>
              <div>
                <input ref={csvRef} type="file" accept=".csv" onChange={handleBulkUpload} className="hidden" data-testid="csv-file-input" />
                <Button onClick={() => csvRef.current?.click()} disabled={uploading} className="bg-[#10B981] hover:bg-[#059669]" data-testid="upload-csv-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</> : <><Upload className="w-4 h-4 mr-2" /> Upload CSV</>}
                </Button>
              </div>
            </div>
            <p className="text-xs text-[#64748B]">CSV must have a header row matching the template columns. For vehicles: title, make, model, year, price, condition, mileage, fuel_type, transmission, color, etc.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Type Filter */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilterType("")}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${!filterType ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
          data-testid="filter-all">All</button>
        {LISTING_TYPES.map((t) => (
          <button key={t.value} onClick={() => setFilterType(t.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filterType === t.value ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
            data-testid={`filter-${t.value}`}>{t.label}</button>
        ))}
        <span className="px-3 py-1.5 text-sm text-[#64748B]">{items.length} items</span>
      </div>

      {/* Editor */}
      <AnimatePresence>
        {showEditor && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white">{editingId ? "Edit Item" : "New Item"}</h2>
              <button onClick={() => setShowEditor(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              {/* Type Selector */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Listing Type</label>
                <div className="flex flex-wrap gap-2">
                  {LISTING_TYPES.map((t) => (
                    <button key={t.value} onClick={() => setForm(p => ({ ...p, listing_type: t.value }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${form.listing_type === t.value ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
                      data-testid={`type-${t.value}`}>{t.label}</button>
                  ))}
                </div>
              </div>
              {/* Common Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Title *</label>
                  <Input value={form.title || ""} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Item title..." className="bg-white/5 border-white/10 text-white" data-testid="inventory-title" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Price</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                    <Input type="number" value={form.price || ""} onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))}
                      placeholder="0" className="pl-10 bg-white/5 border-white/10 text-white" data-testid="inventory-price" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Condition</label>
                  <Input value={form.condition || ""} onChange={(e) => setForm(p => ({ ...p, condition: e.target.value }))}
                    placeholder="New, Used, Excellent" className="bg-white/5 border-white/10 text-white" data-testid="inventory-condition" />
                </div>
              </div>
              {/* Type-Specific Fields */}
              {typeFields.length > 0 && (
                <div>
                  <p className="text-xs text-[#7C3AED] font-medium uppercase tracking-wider mb-3">{form.listing_type.replace("-", " ")} Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {typeFields.map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-[#E2E8F0] mb-1">{f.label}</label>
                        <Input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                          placeholder={f.ph} className="bg-white/5 border-white/10 text-white" data-testid={`field-${f.key}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Description, Features, Location */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Description</label>
                <textarea value={form.description || ""} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe this item..." rows={3}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]" data-testid="inventory-description" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Features (comma-separated)</label>
                <Input value={typeof form.features === "string" ? form.features : (form.features || []).join(", ")} onChange={(e) => setForm(p => ({ ...p, features: e.target.value }))}
                  placeholder="Leather Seats, Sunroof, Navigation" className="bg-white/5 border-white/10 text-white" data-testid="inventory-features" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">City</label>
                  <Input value={form.city || ""} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))}
                    placeholder="City" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">State</label>
                  <Input value={form.state || ""} onChange={(e) => setForm(p => ({ ...p, state: e.target.value }))}
                    placeholder="State" className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">ZIP</label>
                  <Input value={form.zip_code || ""} onChange={(e) => setForm(p => ({ ...p, zip_code: e.target.value }))}
                    placeholder="ZIP" className="bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              {/* Status */}
              <div className="flex gap-3">
                {["active", "sold", "inactive"].map((s) => (
                  <button key={s} onClick={() => setForm(p => ({ ...p, status: s }))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${form.status === s ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
                    data-testid={`status-${s}`}>{s}</button>
                ))}
              </div>
              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Item Image</label>
                <FileUpload category="inventory" accept="image/*" label="Upload Image" currentUrl={form.image_url || (form.images?.[0])}
                  onUploaded={(url) => setForm(p => ({ ...p, image_url: url, images: [url] }))} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditor(false)} className="border-white/20 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="save-item-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingId ? "Update" : "Add to Marketplace"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inventory Grid */}
      {items.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <motion.div key={item.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="bg-[#0F172A] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors group">
              {(item.images?.[0] || item.image_url) ? (
                <img src={item.images?.[0] || item.image_url} alt={item.title} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-white/5 flex items-center justify-center">
                  <Package className="w-10 h-10 text-[#64748B]" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{item.title}</h3>
                    <p className="text-xs text-[#7C3AED] capitalize">{item.listing_type?.replace("-", " ")}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize flex-shrink-0 ${statusColors[item.status] || ""}`}>
                    {item.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-3 text-sm">
                    {item.price != null && <span className="text-[#10B981] font-semibold">${Number(item.price).toLocaleString()}</span>}
                    {item.year && <span className="text-[#94A3B8]">{item.year}</span>}
                    {item.city && <span className="text-[#64748B] text-xs">{item.city}, {item.state}</span>}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-white/10 text-[#94A3B8] hover:text-white" data-testid={`edit-${item.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400" data-testid={`delete-${item.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : !showEditor ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-12 text-center">
          <Package className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Inventory Yet</h2>
          <p className="text-[#94A3B8] max-w-md mx-auto mb-6">Add items individually or use CSV bulk upload to import your entire inventory at once.</p>
          <div className="flex justify-center gap-3">
            <Button onClick={openNew} className="bg-[#7C3AED] hover:bg-[#8B5CF6]"><Plus className="w-4 h-4 mr-2" /> Add Item</Button>
            <Button onClick={() => setShowBulk(true)} variant="outline" className="border-white/20 text-white hover:bg-white/5"><Upload className="w-4 h-4 mr-2" /> Bulk Upload</Button>
          </div>
        </motion.div>
      ) : null}
    </div>
  );
}
